import React from 'react';
import { CheckIcon } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullseye, faBolt, faStore, faCircleInfo, faUsers, faCrosshairs, faVideo, faMobileScreen, faBox, faHandshake } from '@fortawesome/free-solid-svg-icons';
import { Card, CardContent } from './ui/Card';
import { FUNNEL_TYPES, TEMPLATES } from '../lib/data';
const OUTCOMES = [{
  id: 'Get more followers',
  icon: faUsers
}, {
  id: 'Find customers',
  icon: faCrosshairs
}, {
  id: 'Do a live video',
  icon: faVideo
}, {
  id: 'Sell digital stuff',
  icon: faMobileScreen
}, {
  id: 'Sell physical stuff',
  icon: faBox
}, {
  id: 'Sell my services',
  icon: faHandshake
}];
// Dynamically extract categories from TEMPLATES to ensure they always match
const CATEGORIES = Array.from(new Set(TEMPLATES.map(t => t.category))).sort();
interface FilterSidebarProps {
  selectedOutcome: string | null;
  onSelectOutcome: (outcome: string) => void;
  selectedFunnelTypes: string[];
  onToggleFunnelType: (id: string) => void;
  selectedCategories: string[];
  onToggleCategory: (category: string) => void;
}
export function FilterSidebar({
  selectedOutcome,
  onSelectOutcome,
  selectedFunnelTypes,
  onToggleFunnelType,
  selectedCategories,
  onToggleCategory
}: FilterSidebarProps) {
  return <div className="w-full md:w-64 flex-shrink-0 space-y-5" data-id="element-1">
      <Card className="border-border shadow-sm" data-id="element-2">
        <CardContent className="p-5 space-y-6" data-id="element-3">
          {/* Outcome */}
          <div data-id="element-4">
            <h3 className="font-semibold text-foreground mb-3 text-sm flex items-center gap-2" data-id="element-5">
              <FontAwesomeIcon icon={faBullseye} className="text-muted-foreground w-4 h-4" data-id="element-6" />
              What's your goal?
            </h3>
            <div className="space-y-2.5" data-id="element-7">
              {OUTCOMES.map(outcome => {
              const isChecked = selectedOutcome === outcome.id;
              return <label key={outcome.id} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => onSelectOutcome(outcome.id)} data-id="element-8">
                    <div className={`w-4 h-4 flex-shrink-0 rounded-full border flex items-center justify-center transition-colors ${isChecked ? 'bg-blue-600 border-blue-600' : 'border-input group-hover:border-blue-400'}`} data-id="element-9">
                      {isChecked && <div className="w-2 h-2 rounded-full bg-white" data-id="element-10" />}
                    </div>
                    <FontAwesomeIcon icon={outcome.icon} className={`w-3.5 h-3.5 ${isChecked ? 'text-blue-600' : 'text-muted-foreground group-hover:text-foreground'}`} data-id="element-11" />
                    <span className={`text-sm transition-colors whitespace-nowrap ${isChecked ? 'text-foreground font-medium' : 'text-muted-foreground group-hover:text-foreground'}`} data-id="element-12">
                      {outcome.id}
                    </span>
                  </label>;
            })}
            </div>
          </div>

          {/* Funnel Type */}
          <div data-id="element-13">
            <h3 className="font-semibold text-foreground mb-3 text-sm flex items-center gap-2" data-id="element-14">
              <FontAwesomeIcon icon={faBolt} className="text-muted-foreground w-4 h-4" data-id="element-15" />
              What does it do?
            </h3>
            <div className="space-y-2.5" data-id="element-16">
              {FUNNEL_TYPES.map(type => {
              const isChecked = selectedFunnelTypes.includes(type.id);
              return <label key={type.id} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => onToggleFunnelType(type.id)} data-id="element-17">
                    <div className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center transition-colors ${isChecked ? 'bg-blue-600 border-blue-600' : 'border-input group-hover:border-blue-400'}`} data-id="element-18">
                      {isChecked && <CheckIcon className="w-3 h-3 text-white" data-id="element-19" />}
                    </div>
                    <span className={`text-sm transition-colors whitespace-nowrap ${isChecked ? 'text-foreground font-medium' : 'text-muted-foreground group-hover:text-foreground'}`} data-id="element-20">
                      {type.name}
                    </span>
                  </label>;
            })}
            </div>
          </div>

          {/* Category */}
          <div data-id="element-21">
            <h3 className="font-semibold text-foreground mb-3 text-sm flex items-center gap-2" data-id="element-22">
              <FontAwesomeIcon icon={faStore} className="text-muted-foreground w-4 h-4" data-id="element-23" />
              Your business type
            </h3>
            <div className="space-y-2.5" data-id="element-24">
              {CATEGORIES.map(category => {
              const isChecked = selectedCategories.includes(category);
              return <label key={category} className="flex items-center gap-2.5 cursor-pointer group" onClick={() => onToggleCategory(category)} data-id="element-25">
                    <div className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center transition-colors ${isChecked ? 'bg-blue-600 border-blue-600' : 'border-input group-hover:border-blue-400'}`} data-id="element-26">
                      {isChecked && <CheckIcon className="w-3 h-3 text-white" data-id="element-27" />}
                    </div>
                    <span className={`text-sm transition-colors whitespace-nowrap ${isChecked ? 'text-foreground font-medium' : 'text-muted-foreground group-hover:text-foreground'}`} data-id="element-28">
                      {category}
                    </span>
                  </label>;
            })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Tips Section */}
      <Card className="bg-blue-50 border-blue-100 shadow-none" data-id="element-29">
        <CardContent className="p-4 space-y-2" data-id="element-30">
          <h3 className="text-sm font-semibold flex items-center gap-2 text-blue-800" data-id="element-31">
            <FontAwesomeIcon icon={faCircleInfo} className="text-blue-600" data-id="element-32" />
            Hey!
          </h3>
          <p className="text-xs text-blue-700 leading-relaxed" data-id="element-33">
            Not sure what to pick? Just hit the big "Help Me Pick" button above
            — we'll figure it out together!
          </p>
        </CardContent>
      </Card>
    </div>;
}